import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Briefcase, ArrowLeft, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { JobApplicationForm } from "@/components/JobApplicationForm";

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  description: string;
  requirements: string;
  responsibilities: string;
  salary_range?: string;
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);

  // Call useSEO at the top level with dynamic values
  useSEO({
    title: job ? `${job.title} - ${job.location} | Ommni Careers` : 'Job Details | Ommni Careers',
    description: job ? `Join Ommni as a ${job.title} in ${job.location}. ${job.description?.substring(0, 150)}...` : 'View job details and apply for positions at Ommni.',
    keywords: job ? `${job.title}, ${job.department}, careers, jobs, ${job.location}` : 'careers, jobs, employment',
    canonical: `https://ommni.io/careers/job/${id}`,
  });

  useEffect(() => {
    if (id) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('id', id)
        .eq('published', true)
        .maybeSingle();

      if (error) throw error;
      setJob(data);
    } catch (error: any) {
      console.error('Error fetching job:', error);
      toast({
        title: "Error loading job details",
        description: "This position may no longer be available.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold">Job Not Found</h1>
            <p className="text-muted-foreground">This position may no longer be available.</p>
            <Button asChild>
              <Link to="/careers">
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to Careers
              </Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {job && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": job.title,
            "description": job.description,
            "datePosted": new Date().toISOString(),
            "employmentType": job.employment_type.toUpperCase().replace(' ', '_'),
            "hiringOrganization": {
              "@type": "Organization",
              "name": "Ommni",
              "sameAs": "https://ommni.io",
              "logo": "https://ommni.io/assets/ommni-logo.png"
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": job.location
              }
            },
            "baseSalary": job.salary_range ? {
              "@type": "MonetaryAmount",
              "currency": "USD",
              "value": {
                "@type": "QuantitativeValue",
                "value": job.salary_range
              }
            } : undefined,
            "responsibilities": job.responsibilities,
            "qualifications": job.requirements,
            "occupationalCategory": job.department,
            "applicationContact": {
              "@type": "ContactPoint",
              "contactType": "Recruitment",
              "url": "https://ommni.io/contact"
            }
          })}
        </script>
      )}
      
      <div className="flex-1 pt-32 pb-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <Button variant="ghost" asChild className="mb-6">
            <Link to="/careers">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to All Positions
            </Link>
          </Button>

          <div className="space-y-12">
            {/* Header */}
            <div className="space-y-6 pb-8 border-b border-border">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight">{job.title}</h1>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <span className="font-medium">{job.department}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="font-medium">{job.location}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="font-medium">{job.employment_type}</span>
                </div>
                {job.salary_range && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <span className="font-medium">{job.salary_range}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold">About the Role</h2>
              <div className="text-muted-foreground text-lg leading-relaxed prose prose-lg prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {job.description}
                </ReactMarkdown>
              </div>
            </div>

            {/* Responsibilities */}
            <div className="space-y-4 p-8 rounded-2xl bg-muted/30">
              <h2 className="text-3xl font-bold">Responsibilities</h2>
              <div className="text-muted-foreground text-lg leading-relaxed prose prose-lg prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {job.responsibilities}
                </ReactMarkdown>
              </div>
            </div>

            {/* Requirements */}
            <div className="space-y-4 p-8 rounded-2xl bg-muted/30">
              <h2 className="text-3xl font-bold">Requirements</h2>
              <div className="text-muted-foreground text-lg leading-relaxed prose prose-lg prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {job.requirements}
                </ReactMarkdown>
              </div>
            </div>

            {/* Apply CTA */}
            <div className="text-center py-12 px-8 rounded-2xl bg-gradient-to-br from-primary/10 to-purple-500/10 border-2 border-primary/20">
              <h2 className="text-3xl font-bold mb-4">Ready to Apply?</h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                Submit your application and join our team
              </p>
              <Button 
                size="lg" 
                onClick={() => setApplicationDialogOpen(true)}
              >
                Apply Now
              </Button>
            </div>

            {/* Application Form Dialog */}
            <JobApplicationForm
              jobId={job.id}
              jobTitle={job.title}
              open={applicationDialogOpen}
              onOpenChange={setApplicationDialogOpen}
            />
          </div>
        </div>
      </div>

      <Breadcrumbs />
      <Footer />
    </div>
  );
}
